import { getModelName } from '@/lib/construct-form/utils/helpers';
import type {
  BaseInstance as BaseConstructInstance,
  ContentType,
} from '@pigello/construct-core';
import { type BaseInstance, type ModelName } from '@pigello/pigello-matrix';
import { AddressItem } from './address-item';
import { ApartmentItem } from './apartment';
import { ApartmentContractItem } from './apartment-contract';
import { ApartmentListingApplicationItem } from './apartment-listing-application';
import { ApartmentQueueItem } from './apartment-queue';
import { ApartmentRequirementProfileItem } from './apartment-requirement-profile';
import { ApartmentTermsItem } from './apartment-terms-item';
import { ApartmentVacancyItem } from './apartment-vacany-item';
import { ArticleItem } from './article';
import { ArticleCategoryItem } from './article-category-item';
import { AutomaticContractInvoicingSettingItem } from './automatic-contract-invoicing-setting';
import { BlockContractItem } from './block-contract-item';
import { BrfApartmentInvoicingConfigurationItem } from './brf-apartment-invoicing-configuration';
import { BrfApartmentItem } from './brf-apartment-item';
import { BrfCompanyItem } from './brf-company';
import { BuildingItem } from './building';
import { CancellationReasonItem } from './cancellation-reason';
import { CommonAreaItem } from './common-area';
import { CommunityItem } from './community';
import { CompanyItem } from './company';
import { ComponentConditionValueItem } from './component-condition-value';
import { ComponentModelItem } from './componentmodel';
import { ComponentTypeItem } from './componenttype';
import { ContractCancellationConfigurationItem } from './contract-cancellation-configuration';
import { CustomErrandFieldTypeItem } from './custom-errand-field-type';
import { CustomerEarningsSettingsItem } from './customer-earnings-setting';
import { DebtInvoiceItem } from './debtinvoice';
import { ErrandActionDefaultConfigItem } from './errand-action-default-item';
import { GeneralErrandActionItem } from './errandaction';
import { ExternalClientItem } from './externalclient';
import { ExternalSupplierItem } from './externalsupplier';
import { GeneralErrandDefaultDescriptionItem } from './general-errand-default-description-item';
import { GeneralErrandItem } from './general-errand-item';
import { getGeneralErrandTypeItem } from './general-errand-type';
import { IndexationSettingItem } from './indexation-settings';
import { IndexationTableItem } from './indexation-table';
import { IndustrialPremisesItem } from './industrial-premises';
import { IndustrialPremisesContractItem } from './industrial-premises-contract';
import { IndustrialPremisesVacancyItem } from './industrialpremises-vacany-item';
import { InspectionErrandFaultItem } from './inspection-errand-fault-item';
import { InspectionErrandProtocolSettingItem } from './inspection-errand-protocol-setting';
import { InspectionErrandSettingItem } from './inspection-errand-setting-item';
import { InvoiceItem } from './invoice';
import { PaymentTermsSettingItem } from './invoicing-paymentterms-setting-item';
import { MarketApartmentItem } from './market-apartment';
import { OrganizationUserItem } from './organization-user';
import { OtherContractItem } from './other-contract';
import { OutdoorAreaItem } from './outdoor-area';
import { OutdoorSectionItem } from './outdoor-section';
import { OutdoorSectionContractItem } from './outdoor-section-contract';
import { OutdoorSectionVacancyItem } from './outdoorsections-vacany-item';
import { ParkingLotItem } from './parking-lot';
import { ParkingSpotItem } from './parking-spot';
import { ParkingSpotContractItem } from './parking-spot-contract';
import { ParkingSpotVacancyItem } from './parkingspot-vacany-item';
import { PaymentMethodItem } from './paymentmethod';
import { QueueRuleItem } from './queue-rule-item';
import { QuotaCollectionItem } from './quota-collection';
import { QuotaFeeItem } from './quotafee-item';
import { RealestateItem } from './real-estate';
import { RegionItem } from './region';
import { RevenueRowItem } from './revenue-row-item';
import { RoleItem } from './role';
import { RoomCategoryItem } from './roomcategory';
import { SegmentItem } from './segment';
import { SendOutItem } from './send-out';
import { SensorSpecificationItem } from './sensor-specification';
import { SignableDocumentItem } from './signable-document';
import { SubTenantItem } from './sub-tenant';
import { TagItem } from './tag';
import { TaxationRecordItem } from './taxation-record';
import { TenantItem } from './tenant';
import { TenantApplicantItem } from './tenantapplicant';
import type { RelationFieldProps, RelationFieldPropsV2 } from './types';
import { VacancyInvoiceItem } from './vacancy-invoice-item';
import { WorkEngagementItem } from './workengagement';

export function getSelectItemFromModelName<T extends BaseInstance>(
  modelName: ModelName
): RelationFieldProps<T> {
  const map: {
    [K in ModelName]?: RelationFieldProps<any>;
  } = {
    article: ArticleItem,
    apartmentvacancynotation: ApartmentVacancyItem,
    industrialpremisesvacancynotation: IndustrialPremisesVacancyItem,
    outdoorsectionvacancynotation: OutdoorSectionVacancyItem,
    parkingspotvacancynotation: ParkingSpotVacancyItem,
    building: BuildingItem,
    blockcontract: BlockContractItem,
    brfapartment: BrfApartmentItem,
    outdoorarea: OutdoorAreaItem,
    parkinglot: ParkingLotItem,
    apartment: ApartmentItem,
    industrialpremises: IndustrialPremisesItem,
    parkingspot: ParkingSpotItem,
    outdoorsection: OutdoorSectionItem,
    commonarea: CommonAreaItem,
    region: RegionItem,
    realestate: RealestateItem,
    community: CommunityItem,
    company: CompanyItem,
    tenant: TenantItem,
    apartmentcontract: ApartmentContractItem,
    outdoorsectioncontract: OutdoorSectionContractItem,
    parkingspotcontract: ParkingSpotContractItem,
    industrialpremisescontract: IndustrialPremisesContractItem,
    organizationuser: OrganizationUserItem,
    contractcancellationreason: CancellationReasonItem,
    sensorspecification: SensorSpecificationItem,
    othercontract: OtherContractItem,
    workengagement: WorkEngagementItem,
    signabledocument: SignableDocumentItem,
    tag: TagItem,
    indexationtable: IndexationTableItem,
    role: RoleItem,
    segment: SegmentItem,
    invoice: InvoiceItem,
    paymentmethod: PaymentMethodItem,
    articlecategory: ArticleCategoryItem,
    quotacollection: QuotaCollectionItem,
    brfcompany: BrfCompanyItem,
    taxationrecord: TaxationRecordItem,
    apartmentrevenuerow: RevenueRowItem('apartmentrevenuerow'),
    parkingspotrevenuerow: RevenueRowItem('parkingspotrevenuerow'),
    industrialpremisesrevenuerow: RevenueRowItem(
      'industrialpremisesrevenuerow'
    ),
    outdoorsectionrevenuerow: RevenueRowItem('outdoorsectionrevenuerow'),
    brfapartmentrevenuerow: RevenueRowItem('brfapartmentrevenuerow'),
    quotafee: QuotaFeeItem,
    indexationsetting: IndexationSettingItem,
    automaticcontractinvoicingsetting: AutomaticContractInvoicingSettingItem,
    paymenttermssetting: PaymentTermsSettingItem,
    brfapartmentinvoiceconfiguration: BrfApartmentInvoicingConfigurationItem,
    externalclient: ExternalClientItem,
    debtinvoice: DebtInvoiceItem,
    customerearningsrevenuesetting: CustomerEarningsSettingsItem,
    address: AddressItem,
    vacancyinvoice: VacancyInvoiceItem,
    generalerranddefaultdescription: GeneralErrandDefaultDescriptionItem,
    errandactiondefault: ErrandActionDefaultConfigItem,
    componentconditionvalue: ComponentConditionValueItem,
    roomcategory: RoomCategoryItem,
    externalsupplier: ExternalSupplierItem,
    componenttype: ComponentTypeItem,
    componentmodel: ComponentModelItem,
    generalerrandaction: GeneralErrandActionItem,
    generalerrandtype: getGeneralErrandTypeItem(),
    generalerrandsubtype: getGeneralErrandTypeItem(true),
    customerrandfieldtype: CustomErrandFieldTypeItem,
    inspectionerrandprotocolsetting: InspectionErrandProtocolSettingItem,
    contractcancellationconfiguration: ContractCancellationConfigurationItem,
    inspectionerrandfault: InspectionErrandFaultItem,
    inspectionerrandsetting: InspectionErrandSettingItem,
    subtenant: SubTenantItem,
    generalerrand: GeneralErrandItem,
    sendout: SendOutItem,
    apartmentterms: ApartmentTermsItem,
    apartmentqueue: ApartmentQueueItem,
    queuerules: QueueRuleItem,
    apartmentrequirementprofile: ApartmentRequirementProfileItem,
    tenantapplicant: TenantApplicantItem,
    apartmentlistingapplication: ApartmentListingApplicationItem,
    marketapartment: MarketApartmentItem,
  };

  return (
    (map[modelName] as RelationFieldProps<T>) ?? {
      filters: [],
      renderItem: () => ({
        title: 'Not implemented',
        badge: undefined,
        descriptionItems: modelName,
      }),
    }
  );
}

export function getSelectItemFromContentType<T extends BaseConstructInstance>(
  contentType: ContentType
): RelationFieldPropsV2<T> {
  const modelName = getModelName(contentType);

  if (!modelName) {
    return {
      filters: [],
      renderItem: () => ({
        title: 'Not implemented',
        badge: undefined,
        descriptionItems: contentType,
      }),
    };
  }

  const selectItem = getSelectItemFromModelName(modelName);

  return {
    nested: selectItem.nested?.map(String),
    filters: selectItem.filters,
    renderItem: (item, isRelationField) =>
      selectItem.renderItem(
        item as unknown as BaseInstance,
        isRelationField,
        modelName
      ),
  };
}
